/**
 * XSS Protection Utilities
 * Provides input validation, sanitization, and output encoding
 */

/**
 * Escape HTML special characters to prevent XSS
 * @param {string} str - Input string to escape
 * @returns {string} - Escaped string safe for HTML output
 */
export function escapeHtml(str) {
  if (typeof str !== 'string') return str;
  
  const htmlEscapes = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;'
  };
  
  return str.replace(/[&<>"'/]/g, (match) => htmlEscapes[match]);
}

/**
 * Sanitize input by removing potentially dangerous characters
 * @param {string} input - Input string to sanitize
 * @param {Object} options - Sanitization options
 * @returns {string} - Sanitized string
 */
export function sanitizeInput(input, options = {}) {
  if (typeof input !== 'string') return input;
  
  const {
    allowHtml = false,
    maxLength = null,
    stripScripts = true,
    stripEvents = true,
    aggressive = false // New option for aggressive sanitization
  } = options;
  
  let sanitized = input;
  
  // Remove script tags and content
  if (stripScripts) {
    sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  }
  
  // Remove event handlers (onclick, onload, etc.)
  if (stripEvents) {
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '');
  }
  
  // Remove javascript: protocols
  sanitized = sanitized.replace(/javascript:/gi, '');
  
  // Remove data: URLs (can contain scripts)
  sanitized = sanitized.replace(/data:\s*text\/html/gi, 'data:text/plain');
  
  // Only escape HTML if aggressive mode is enabled or HTML is not allowed and we're doing final sanitization
  if (aggressive && !allowHtml) {
    sanitized = escapeHtml(sanitized);
  }
  
  // Limit length only if maxLength is specified
  if (maxLength && sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }
  
  return sanitized.trim();
}

/**
 * Validate and sanitize specific input types
 */
export const validators = {
  /**
   * Email validation and sanitization
   */
  email(email) {
    if (!email || typeof email !== 'string') return '';
    
    const sanitized = email.toLowerCase().trim();
    
    // Only remove script tags and dangerous content, preserve normal characters
    let cleaned = sanitized.replace(/<script[^>]*>.*?<\/script>/gi, '');
    cleaned = cleaned.replace(/javascript:/gi, '');
    
    // Allow all normal email characters during typing
    return cleaned.substring(0, 100);
  },
  
  /**
   * Name validation and sanitization
   */
  name(name) {
    if (!name || typeof name !== 'string') return '';
    
    const sanitized = name.trim();
    
    // Only remove script tags and dangerous content, preserve normal characters
    let cleaned = sanitized.replace(/<script[^>]*>.*?<\/script>/gi, '');
    cleaned = cleaned.replace(/javascript:/gi, '');
    
    // Allow all normal characters including punctuation during typing
    return cleaned.substring(0, 100);
  },
  
  /**
   * Phone number validation and sanitization
   */
  phone(phone) {
    if (!phone || typeof phone !== 'string') return '';
    
    const sanitized = phone.trim();
    
    // Only remove script tags and dangerous content
    let cleaned = sanitized.replace(/<script[^>]*>.*?<\/script>/gi, '');
    cleaned = cleaned.replace(/javascript:/gi, '');
    
    // Allow all normal phone characters during typing
    return cleaned.substring(0, 20);
  },
  
  /**
   * Generic text validation - permissive during typing
   */
  text(text, maxLength = null) {
    if (!text || typeof text !== 'string') return '';
    
    return sanitizeInput(text, { 
      allowHtml: false, 
      maxLength,
      stripScripts: true,
      stripEvents: true,
      aggressive: false // Don't escape HTML during typing
    });
  },

  /**
   * URL-aware text validation that preserves URLs while sanitizing other content
   */
  textWithUrls(text, maxLength = 2000) {
    if (!text || typeof text !== 'string') return '';
    
    // URL regex pattern to detect URLs
    const urlRegex = /(https?:\/\/[^\s<>"']+)/gi;
    
    // Split text into parts, separating URLs from regular text
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = urlRegex.exec(text)) !== null) {
      // Add text before URL (sanitized)
      if (match.index > lastIndex) {
        const textBefore = text.slice(lastIndex, match.index);
        parts.push(sanitizeInput(textBefore, { 
          allowHtml: false, 
          maxLength: textBefore.length,
          stripScripts: true,
          stripEvents: true,
          aggressive: false
        }));
      }
      
      // Add URL as-is (but validate it's actually a URL)
      const url = match[0];
      try {
        const parsed = new URL(url);
        // Only allow http and https protocols
        if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
          parts.push(url);
        } else {
          // If not a valid http/https URL, sanitize it
          parts.push(sanitizeInput(url, { 
            allowHtml: false, 
            maxLength: url.length,
            stripScripts: true,
            stripEvents: true,
            aggressive: false
          }));
        }
      } catch {
        // If URL parsing fails, sanitize it
        parts.push(sanitizeInput(url, { 
          allowHtml: false, 
          maxLength: url.length,
          stripScripts: true,
          stripEvents: true,
          aggressive: false
        }));
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text after last URL (sanitized)
    if (lastIndex < text.length) {
      const textAfter = text.slice(lastIndex);
      parts.push(sanitizeInput(textAfter, { 
        allowHtml: false, 
        maxLength: textAfter.length,
        stripScripts: true,
        stripEvents: true,
        aggressive: false
      }));
    }
    
    // If no URLs found, just sanitize the entire text
    if (parts.length === 0) {
      return sanitizeInput(text, { 
        allowHtml: false, 
        maxLength,
        stripScripts: true,
        stripEvents: true,
        aggressive: false
      });
    }
    
    // Join all parts and limit length
    const result = parts.join('');
    return result.length > maxLength ? result.substring(0, maxLength) : result;
  },
  
  /**
   * URL validation and sanitization
   */
  url(url) {
    if (!url || typeof url !== 'string') return '';
    
    try {
      const parsed = new URL(url);
      // Only allow http and https protocols
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return '';
      }
      return parsed.toString();
    } catch {
      return '';
    }
  }
};

/**
 * Vue directive for automatic input sanitization
 */
export const xssDirective = {
  mounted(el, binding) {
    const inputType = binding.arg || 'text';
    const validator = validators[inputType] || validators.text;
    
    // Only sanitize on blur to allow natural typing
    el.addEventListener('blur', (event) => {
      const original = event.target.value;
      const sanitized = validator(original);
      if (sanitized !== original) {
        event.target.value = sanitized;
        // Trigger Vue's input event to update v-model
        event.target.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
    
    // Light safety check on input - only remove obviously dangerous content
    el.addEventListener('input', (event) => {
      const value = event.target.value;
      // Only block obvious script injections, allow normal typing
      if (value.includes('<script') || value.includes('javascript:') || value.includes('data:text/html')) {
        const cleaned = value.replace(/<script[^>]*>.*?<\/script>/gi, '')
                             .replace(/javascript:/gi, '')
                             .replace(/data:\s*text\/html/gi, 'data:text/plain');
        if (cleaned !== value) {
          event.target.value = cleaned;
          event.target.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    });
  }
};

/**
 * Safe innerHTML replacement
 * @param {string} html - HTML content to render safely
 * @returns {string} - Sanitized HTML
 */
export function safeHtml(html) {
  if (!html || typeof html !== 'string') return '';
  
  // Create a temporary element to parse and sanitize
  const temp = document.createElement('div');
  temp.textContent = html; // This automatically escapes HTML
  return temp.innerHTML;
}

/**
 * Final sanitization for data being saved or output
 * This is more aggressive and should be used when data is being persisted or displayed
 * @param {string} input - Input to sanitize
 * @param {Object} options - Sanitization options
 * @returns {string} - Sanitized output
 */
export function finalSanitize(input, options = {}) {
  if (!input || typeof input !== 'string') return input;
  
  return sanitizeInput(input, {
    ...options,
    aggressive: true // Enable aggressive sanitization
  });
}

export default {
  escapeHtml,
  sanitizeInput,
  finalSanitize,
  validators,
  xssDirective,
  safeHtml
};
